export const en = {
  "header": {
    "brand": "CikaStudio",
    "tools": "Tools",
    "games": "Games",
    "about": "About"
  },
  "nav": {
    "tools": "Tools",
    "games": "Games",
    "about": "About"
  },
  "actions": {
    "copy": "Copy",
    "download": "Download",
    "clear": "Clear",
    "copied": "Copied!",
    "copyAll": "Copy All",
    "downloadTxt": "Download TXT",
    "generate": "Generate",
    "regenerate": "Regenerate",
    "format": "Format",
    "minify": "Minify",
    "validate": "Validate",
    "convert": "Convert",
    "reset": "Reset"
  },
  "footer": {
    "privacy": "Privacy",
    "terms": "Terms",
    "email": "Contact",
    "moreTools": "More Tools",
    "copyright": "© {year} CikaStudio. All rights reserved.",
    "trust": {
      "local": "🔒 Pure local processing, zero data upload",
      "free": "✅ Free to use, no registration required",
      "formats": "📐 Supports PNG/SVG/PDF HD export"
    },
    "relatedTools": "Related Tools"
  },
  "language": {
    "en": "English",
    "zh": "中文"
  },
  "theme": {
    "light": "Light",
    "dark": "Dark"
  },
  "common": {
    "clientSide": "100% client-side",
    "noUpload": "No data upload",
    "noAccount": "No account required",
    "loading": "Loading...",
    "error": "Error",
    "success": "Success"
  },
  "qr": {
    "title": "QR Code Generator",
    "description": "Generate free QR codes for URLs, text, WiFi, vCard, email, phone, and SMS. Zero registration, zero ads, pure client-side processing.",
    "types": {
      "url": "URL",
      "text": "Text",
      "wifi": "WiFi",
      "email": "Email",
      "phone": "Phone",
      "sms": "SMS",
      "vcard": "vCard"
    },
    "input": {
      "urlLabel": "Website URL",
      "urlPlaceholder": "https://example.com",
      "textLabel": "Text Content",
      "textPlaceholder": "Enter your text here...",
      "wifi": {
        "ssidLabel": "Network Name (SSID)",
        "ssidPlaceholder": "MyWiFi",
        "passwordLabel": "Password",
        "passwordPlaceholder": "WiFi password",
        "encryptionLabel": "Security",
        "hiddenLabel": "Hidden Network"
      },
      "email": {
        "emailLabel": "Email Address",
        "emailPlaceholder": "someone@example.com",
        "subjectLabel": "Subject",
        "subjectPlaceholder": "Email subject",
        "bodyLabel": "Body",
        "bodyPlaceholder": "Email body text..."
      },
      "phoneLabel": "Phone Number",
      "phonePlaceholder": "+1 234 567 8900",
      "sms": {
        "phoneLabel": "Phone Number",
        "phonePlaceholder": "+1 234 567 8900",
        "messageLabel": "Message",
        "messagePlaceholder": "Message text..."
      },
      "vcard": {
        "nameLabel": "Full Name",
        "namePlaceholder": "John Doe",
        "phoneLabel": "Phone",
        "phonePlaceholder": "+1 234 567 8900",
        "emailLabel": "Email",
        "emailPlaceholder": "john@example.com",
        "orgLabel": "Company",
        "orgPlaceholder": "Company Inc.",
        "titleLabel": "Job Title",
        "titlePlaceholder": "Software Engineer",
        "urlLabel": "Website",
        "urlPlaceholder": "https://example.com",
        "addressLabel": "Address",
        "addressPlaceholder": "123 Main St, City, Country"
      }
    },
    "design": {
      "title": "Design & Options",
      "foregroundColor": "Foreground Color",
      "backgroundColor": "Background Color",
      "presets": "Color Presets",
      "logo": {
        "title": "Logo Overlay",
        "upload": "Upload Logo (PNG/JPG/SVG)",
        "dragDrop": "Drag & drop or click to upload",
        "whiteBorder": "Add white border",
        "borderSize": "Border size",
        "remove": "Remove"
      },
      "errorCorrection": "Error Correction Level",
      "levels": {
        "L": "L (7%)",
        "M": "M (15%)",
        "Q": "Q (25%)",
        "H": "H (30%)"
      },
      "levelHint": "Higher levels allow logo overlay and better scan reliability on damaged prints."
    },
    "preview": {
      "title": "Preview",
      "status": "Live preview · Scan to test",
      "empty": "Enter content to generate QR code",
      "generating": "Generating...",
      "errorCorrection": "EC: {level}"
    },
    "download": {
      "title": "Download",
      "png512": "PNG (512px)",
      "png1024": "PNG (1024px) — Recommended",
      "png2048": "PNG (2048px)",
      "svg": "SVG Vector",
      "pdf": "PDF (A4 Print)",
      "filename": "qr-cikastudio-{type}-{date}"
    },
    "actions": {
      "copyData": "Copy Data",
      "resetConfig": "Reset"
    },
    "errors": {
      "urlInvalid": "Please enter a valid URL starting with http:// or https://",
      "required": "This field is required",
      "contentTooLong": "Content is too long for QR code. Try using a shorter URL."
    },
    "security": {
      "note": "🔒 All processing happens locally in your browser. No data is uploaded to any server."
    },
    "faq": {
      "title": "Frequently Asked Questions",
      "q1": "Are generated QR codes free for commercial use?",
      "a1": "Yes. All static QR codes are generated completely free and can be used for commercial purposes without watermarks.",
      "q2": "Is my QR code data uploaded to a server?",
      "a2": "No. This tool is purely frontend — all generation logic runs locally in your browser. Zero data upload.",
      "q3": "What content types are supported?",
      "a3": "We support URL, plain text, WiFi networks, vCard electronic business cards, email addresses, phone numbers, and SMS.",
      "q4": "Can I customize QR code styles?",
      "a4": "Yes. You can customize foreground/background colors, overlay a logo, and adjust error correction levels. Preset color schemes are also available.",
      "q5": "What export formats are supported?",
      "a5": "PNG (512px/1024px/2048px), SVG vector format, and PDF print format — all free to download."
    },
    "scenarios": {
      "title": "Use Cases",
      "url": {
        "title": "Website Link",
        "desc": "Share your website, blog, or social media with a scannable code."
      },
      "wifi": {
        "title": "WiFi Sharing",
        "desc": "Let guests connect to your network without typing passwords."
      },
      "vcard": {
        "title": "Digital Business Card",
        "desc": "Share contact info professionally with a single scan."
      },
      "email": {
        "title": "Email Pre-fill",
        "desc": "Pre-fill subject and body for support or contact forms."
      },
      "phone": {
        "title": "Quick Dial",
        "desc": "One-scan dialing for customer service or emergency numbers."
      },
      "text": {
        "title": "Plain Text",
        "desc": "Encode any text message, note, or coupon code."
      }
    }
  },
  "content": {
    "privacy": {
      "title": "Privacy & Security",
      "description": "🔒 All QR code generation happens locally in your browser. Your data is never uploaded to any server."
    },
    "guide": {
      "title": "How to Use QR Code Generator",
      "description": "Create scannable QR codes for URLs, text, WiFi, contact cards, and more.",
      "steps": [
        {
          "title": "Choose a QR type",
          "description": "Select from URL, text, WiFi, email, phone, SMS, or vCard contact formats. Each type auto-generates the correct data format."
        },
        {
          "title": "Enter your data",
          "description": "Fill in the fields for your chosen QR type. For WiFi, enter the SSID, password, and encryption type. For URLs, simply paste the link."
        },
        {
          "title": "Download your QR code",
          "description": "Customize colors and size, then download your QR code as PNG or SVG. All QR codes are error-corrected and scannable by any modern smartphone."
        }
      ]
    },
    "features": {
      "title": "Key Features",
      "items": [
        "Support for 7+ QR code types: URL, text, WiFi, email, phone, SMS, vCard",
        "Customizable foreground and background colors",
        "Download as high-resolution PNG or scalable SVG",
        "Real-time preview as you type",
        "Error correction for reliable scanning even with partial damage",
        "100% client-side processing — zero data upload"
      ]
    },
    "faq": {
      "title": "Frequently Asked Questions",
      "items": [
        {
          "question": "Is my QR code data sent to a server?",
          "answer": "No. All QR code generation happens entirely within your browser. Your URLs, WiFi passwords, and contact information never leave your device."
        },
        {
          "question": "Will my QR code work on any phone?",
          "answer": "Yes. The generator produces standard QR codes that can be scanned by any modern smartphone camera app, as well as dedicated QR scanner apps on iOS and Android."
        },
        {
          "question": "Can I use a custom logo in the center?",
          "answer": "Currently the tool focuses on clean, reliable QR codes. For logo embedding, export the SVG and overlay your logo using a design tool like our Image Watermarker."
        }
      ]
    },
    "related": {
      "title": "Related Tools",
      "tools": [
        {
          "name": "UUID Generator",
          "url": "/generator/uuid-generator",
          "description": "Generate universally unique identifiers for APIs and databases."
        },
        {
          "name": "Random Number Generator",
          "url": "/generator/random-number-generator",
          "description": "Generate random integers, decimals, hex, and binary numbers."
        },
        {
          "name": "Base64 Converter",
          "url": "/converter/base64",
          "description": "Encode and decode Base64 strings quickly and securely."
        }
      ]
    }
  }
};
